import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bookmark, Check, Plus, Lock, Loader2 } from "lucide-react";

export const SaveToCollection = ({ mediaId }) => {
  const { user, requireAction } = useAuth();
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const saved = (cols || []).some((c) => c.has);

  const load = useCallback(async () => {
    try { const r = await api.get(`/media/${mediaId}/collections`); setCols(r.data); } catch { setCols([]); }
  }, [mediaId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (c) => {
    setCols((p) => p.map((x) => x.id === c.id ? { ...x, has: !x.has, count: x.count + (x.has ? -1 : 1) } : x));
    try { await api.post(`/collections/${c.id}/toggle`, { media_id: mediaId }); }
    catch { toast.error("Failed"); load(); }
  };

  const createAndAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/collections", { name });
      await api.post(`/collections/${r.data.id}/toggle`, { media_id: mediaId });
      setName("");
      toast.success(`Saved to "${r.data.name}"`);
      load();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  const onOpenChange = (o) => {
    if (o && !requireAction()) return;
    setOpen(o);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="save-to-collection-btn"
          className={`inline-flex items-center gap-1.5 text-sm rounded-full border px-3 py-1.5 transition-colors ${saved ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"}`}>
          <Bookmark className={`w-4 h-4 ${saved ? "fill-primary" : ""}`} /> {saved ? "Saved" : "Save"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 bg-popover border-border p-2" data-testid="save-collection-popover">
        <p className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1">Save to gallery</p>
        <div className="max-h-56 overflow-y-auto">
          {cols === null ? <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            : cols.length === 0 ? <p className="text-sm text-muted-foreground px-2 py-3">No galleries yet — create one below.</p>
            : cols.map((c) => (
              <button key={c.id} data-testid={`save-col-${c.id}`} onClick={() => toggle(c)}
                className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-2 hover:bg-secondary/60 text-left">
                <span className="flex items-center gap-1.5 min-w-0">
                  {c.visibility === "private" && <Lock className="w-3 h-3 text-muted-foreground shrink-0" />}
                  <span className="text-sm truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">· {c.count}</span>
                </span>
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${c.has ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                  {c.has && <Check className="w-3.5 h-3.5" />}
                </span>
              </button>
            ))}
        </div>
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border">
          <input data-testid="save-new-collection" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createAndAdd()}
            placeholder="New gallery…" className="flex-1 bg-secondary/50 border border-border rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-primary" />
          <button data-testid="save-new-collection-btn" onClick={createAndAdd} disabled={busy || !name.trim()}
            className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
